import { Box, Typography } from "@mui/joy";
import { useEffect, useMemo, useRef } from "react";

import {
    formatDate,
    formatHourLabel,
    getEventStartMinutes,
    getHourSlots,
    HOUR_HEIGHT,
    isToday,
    minutesToTop,
} from "./utils";
import { EventBlock } from "./EventBlock";

export interface CalendarEvent {
    id: string;
    title: string;
    date: string;
    time?: string;
    allDay: boolean;
    color: string;
    description?: string;
    projectId?: string;
    taskStatus?: string;
    tags?: Record<string, string>;
}

interface TimeGridViewProps {
    days: Date[];
    events: CalendarEvent[];
    onDateClick: (date: Date) => void;
    onEventClick: (event: CalendarEvent, e: React.MouseEvent) => void;
}

const GRID_BORDER = "rgba(128, 128, 128, 0.18)";

export function TimeGridView({
    days,
    events,
    onDateClick,
    onEventClick,
}: TimeGridViewProps) {
    const hours = useMemo(() => getHourSlots(), []);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const now = new Date();
        const top = Math.max(0, minutesToTop(now.getHours() * 60 + now.getMinutes()) - 80);
        el.scrollTop = top;
    }, []);

    const nowTop = (() => {
        const now = new Date();
        return minutesToTop(now.getHours() * 60 + now.getMinutes());
    })();

    const gridTemplateColumns = `64px repeat(${days.length}, minmax(0, 1fr))`;

    return (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns,
                    borderBottom: "1px solid",
                    borderColor: GRID_BORDER,
                    flexShrink: 0,
                    bgcolor: "background.surface",
                }}
            >
                <Box />
                {days.map((day) => {
                    const today = isToday(day);
                    return (
                        <Box
                            key={formatDate(day)}
                            onClick={() => onDateClick(day)}
                            sx={{
                                py: 1,
                                px: 1,
                                textAlign: "center",
                                borderLeft: "1px solid",
                                borderColor: GRID_BORDER,
                                cursor: "pointer",
                                "&:hover": { bgcolor: "background.level1" },
                            }}
                        >
                            <Typography
                                level="body-xs"
                                sx={{
                                    textTransform: "uppercase",
                                    letterSpacing: "0.4px",
                                    color: today ? "primary.600" : "text.tertiary",
                                    fontWeight: 600,
                                }}
                            >
                                {day.toLocaleDateString("en-US", { weekday: "short" })}
                            </Typography>
                            <Box
                                sx={{
                                    width: 28,
                                    height: 28,
                                    mx: "auto",
                                    mt: 0.25,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    ...(today && {
                                        bgcolor: "primary.solidBg",
                                        color: "primary.solidColor",
                                    }),
                                }}
                            >
                                <Typography
                                    level="body-sm"
                                    sx={{
                                        fontWeight: 600,
                                        color: today ? "inherit" : "text.primary",
                                    }}
                                >
                                    {day.getDate()}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns,
                    borderBottom: "1px solid",
                    borderColor: GRID_BORDER,
                    flexShrink: 0,
                    minHeight: 40,
                    bgcolor: "background.surface",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        pr: 1,
                    }}
                >
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        All day
                    </Typography>
                </Box>
                {days.map((day) => {
                    const dateStr = formatDate(day);
                    const allDayEvents = events.filter(
                        (e) =>
                            e.date === dateStr &&
                            (e.allDay || getEventStartMinutes(e.time, e.allDay) == null),
                    );
                    return (
                        <Box
                            key={`allday-${dateStr}`}
                            onClick={() => onDateClick(day)}
                            sx={{
                                borderLeft: "1px solid",
                                borderColor: GRID_BORDER,
                                px: 0.5,
                                py: 0.5,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                cursor: "pointer",
                                minHeight: 36,
                            }}
                        >
                            {allDayEvents.map((event) => (
                                <EventBlock
                                    key={event.id}
                                    event={event}
                                    compact
                                    onClick={onEventClick}
                                />
                            ))}
                        </Box>
                    );
                })}
            </Box>

            <Box ref={scrollRef} sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns,
                        position: "relative",
                        height: hours.length * HOUR_HEIGHT,
                    }}
                >
                    <Box sx={{ position: "relative" }}>
                        {hours.map((hour) => (
                            <Box
                                key={hour}
                                sx={{
                                    height: HOUR_HEIGHT,
                                    pr: 1,
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    position: "relative",
                                }}
                            >
                                <Typography
                                    level="body-xs"
                                    sx={{
                                        color: "text.tertiary",
                                        transform: "translateY(-50%)",
                                        mt: hour === 0 ? 1 : 0,
                                    }}
                                >
                                    {formatHourLabel(hour)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>

                    {days.map((day) => {
                        const dateStr = formatDate(day);
                        const dayTimedEvents = events.filter((e) => {
                            if (e.date !== dateStr) return false;
                            return getEventStartMinutes(e.time, e.allDay) != null;
                        });
                        const showNow = isToday(day);

                        return (
                            <Box
                                key={`grid-${dateStr}`}
                                onClick={() => onDateClick(day)}
                                sx={{
                                    borderLeft: "1px solid",
                                    borderColor: GRID_BORDER,
                                    position: "relative",
                                    cursor: "pointer",
                                    "&:hover": {
                                        bgcolor: "rgba(128, 128, 128, 0.03)",
                                    },
                                }}
                            >
                                {hours.map((hour) => (
                                    <Box
                                        key={hour}
                                        sx={{
                                            height: HOUR_HEIGHT,
                                            borderBottom: "1px solid",
                                            borderColor: GRID_BORDER,
                                        }}
                                    />
                                ))}

                                {showNow && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            left: 0,
                                            right: 0,
                                            top: nowTop,
                                            zIndex: 2,
                                            display: "flex",
                                            alignItems: "center",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: "50%",
                                                bgcolor: "danger.500",
                                                ml: -0.5,
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                flex: 1,
                                                height: "2px",
                                                bgcolor: "danger.500",
                                            }}
                                        />
                                    </Box>
                                )}

                                {dayTimedEvents.map((event) => {
                                    const start =
                                        getEventStartMinutes(event.time, event.allDay) ?? 0;
                                    const top = minutesToTop(start);
                                    const height = Math.max(HOUR_HEIGHT * 0.75, 28);

                                    return (
                                        <EventBlock
                                            key={event.id}
                                            event={event}
                                            positioned={{ top, height }}
                                            onClick={onEventClick}
                                        />
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
}
